import FirebaseAdapter from "@wc/adapters/firebase";
import {
  userHasPermissionInOrg,
  PermissionsEnum,
  roleToNumeric,
  numericToRole,
} from "@wc/domain/authz";

import { OrgRoles } from "../constants";

/** @typedef { import("@wc/domain/users").AuthnUser } AuthnUser */
/** @typedef { import("../models").OrgListUser } OrgListUser */

/**
 * @param {string} organizationId The id of the organization to check
 * @param {string} _accessUserId The user requesting access (validated server-side)
 * @returns {Promise<OrgListUser[]>} The users in the organization
 */
export async function listOrgMembers(organizationId, _accessUserId) {
  const result = await FirebaseAdapter().functions.getOrgMembers({
    organizationId,
  });

  if (!result.success) {
    throw new Error(`Unable to access organization ${organizationId}`);
  }

  return result.members.map((m) => ({
    id: m.id,
    displayName: m.displayName,
    photoURL: m.photoURL,
    createdAt: m.createdAt ? new Date(m.createdAt) : null,
    role: m.role ? numericToRole(m.role) : null,
  }));
}

/**
 * @param {string} organizationId The organization to look at
 * @param {string} userId The user to remove
 * @param {AuthnUser} accessUser A full authenticated user to check
 */
export async function removeOrgMember(organizationId, userId, accessUser) {
  if (userId === accessUser.id) {
    throw new Error("You cannot delete yourself");
  }

  const hasRemovePermission = await userHasPermissionInOrg(
    accessUser,
    organizationId,
    PermissionsEnum.REMOVE_ORGANIZATION_MEMBER,
  );

  if (!hasRemovePermission) {
    throw new Error("You do not have permission to remove this user");
  }

  // Call the removeFromOrg function
  await FirebaseAdapter().functions.removeFromOrg({
    organizationId,
    targetUserId: userId,
  });
}

/**
 * @param {string} organizationId The organization to look at
 * @param {string} userId The user to operate on
 * @param {string} newRole The new role to assign the user
 * @param {AuthnUser} accessUser A full authenticated user to check
 */
export async function updateOrgMemberRole(
  organizationId,
  userId,
  newRole,
  accessUser,
) {
  if (userId === accessUser.id) {
    throw new Error("You cannot modify your own role");
  }

  if (!OrgRoles.includes(newRole)) {
    throw new Error(
      `invalid role assignment for organization ${organizationId}: ${newRole}`,
    );
  }

  const hasChangePermission = await userHasPermissionInOrg(
    accessUser,
    organizationId,
    PermissionsEnum.MANAGE_ORGANIZATION_ROLES,
  );

  if (!hasChangePermission) {
    throw new Error("You do not have permission to alter this user's role");
  }

  // Call the updateOrgRole function
  await FirebaseAdapter().functions.updateOrgRole({
    organizationId,
    targetUserId: userId,
    newRole: roleToNumeric(newRole),
  });

  // No need for token refresh - the function handles it
}
